import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createUserSchema,
  type CreateUserFormData,
} from "@/schemas/userSchemas";
import { useCreateUser } from "@/hooks/useUsers";
import { applyPhoneMask, removePhoneMask } from "@/lib/utils";
import { useState } from "react";

export const CreateUser = () => {
  const navigate = useNavigate();
  const createUser = useCreateUser();
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: "CITIZEN",
      cpf: "",
      phone: "",
      zipCode: "",
    },
  });

  const searchCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length !== 8) {
      return;
    }

    setIsLoadingCep(true);

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      );
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }

      setValue("street", data.logradouro || "");
      setValue("neighborhood", data.bairro || "");
      setValue("city", data.localidade || "");
      setValue("state", data.uf || "");

      toast.success("Endereço encontrado!");
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Erro ao buscar CEP");
    } finally {
      setIsLoadingCep(false);
    }
  };

  const onSubmit = async (data: CreateUserFormData) => {
    await createUser.mutateAsync(
      {
        ...data,
        phone: removePhoneMask(data.phone),
        cpf: data.cpf.replace(/\D/g, ""),
        zipCode: data.zipCode.replace(/\D/g, ""),
      },
      {
        onSuccess: () => {
          navigate("/users");
          toast.success("Usuário criado com sucesso!");
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.message || "Erro ao criar usuário";
          toast.error(errorMessage);
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Criar Usuário"
        description="Informe os dados do novo usuário"
      />

      <div className="flex-1 overflow-y-auto mt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-white">
            <CardContentUI className="p-4">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Informações de Login
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      title="Email"
                      isRequired
                      type="email"
                      placeholder="usuario@email.com"
                      error={errors.email?.message}
                      {...register("email")}
                    />

                    <Input
                      title="Senha"
                      isRequired
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      error={errors.password?.message}
                      {...register("password")}
                    />

                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Função <span className="text-red-500">*</span>
                          </label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a função" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CITIZEN">Cidadão</SelectItem>
                              <SelectItem value="AGENT">
                                Agente de Campo
                              </SelectItem>
                              <SelectItem value="ADMIN">
                                Administrador
                              </SelectItem>
                              <SelectItem value="TRIAGE">Triagem</SelectItem>
                              <SelectItem value="AUDIT">Auditoria</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.role && (
                            <p className="text-sm text-red-500">
                              {errors.role.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Informações Pessoais
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      title="Nome Completo"
                      isRequired
                      placeholder="João da Silva"
                      error={errors.fullName?.message}
                      {...register("fullName")}
                    />

                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="CPF"
                          isRequired
                          placeholder="000.000.000-00"
                          error={errors.cpf?.message}
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            const maskedValue = value
                              .replace(/(\d{3})(\d)/, "$1.$2")
                              .replace(/(\d{3})(\d)/, "$1.$2")
                              .replace(/(\d{3})(\d{1,2})/, "$1-$2")
                              .replace(/(-\d{2})\d+?$/, "$1");
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="Telefone"
                          isRequired
                          placeholder="(11) 91234-5678"
                          error={errors.phone?.message}
                          value={field.value}
                          onChange={(e) => {
                            const maskedValue = applyPhoneMask(e.target.value);
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold mb-3">Endereço</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Controller
                      name="zipCode"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            CEP <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Input
                              placeholder="00000-000"
                              error={errors.zipCode?.message}
                              value={field.value}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                const maskedValue = value
                                  .replace(/(\d{5})(\d)/, "$1-$2")
                                  .replace(/(-\d{3})\d+?$/, "$1");
                                field.onChange(maskedValue);

                                // Buscar CEP automaticamente quando tiver 8 dígitos
                                if (value.length === 8) {
                                  searchCep(value);
                                }
                              }}
                            />
                            {isLoadingCep && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    />

                    <Input
                      title="Rua"
                      isRequired
                      placeholder="Av. Principal"
                      error={errors.street?.message}
                      {...register("street")}
                    />

                    <Input
                      title="Número"
                      isRequired
                      placeholder="123"
                      error={errors.number?.message}
                      {...register("number")}
                    />

                    <Input
                      title="Bairro"
                      isRequired
                      placeholder="Centro"
                      error={errors.neighborhood?.message}
                      {...register("neighborhood")}
                    />

                    <Input
                      title="Cidade"
                      isRequired
                      placeholder="São Paulo"
                      error={errors.city?.message}
                      {...register("city")}
                    />

                    <Input
                      title="Estado (UF)"
                      isRequired
                      placeholder="SP"
                      maxLength={2}
                      error={errors.state?.message}
                      {...register("state")}
                      onChange={(e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/users")}
                    disabled={createUser.isPending}
                    className="cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createUser.isPending}
                    isLoading={createUser.isPending}
                    className="text-white cursor-pointer"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Criar Usuário
                  </Button>
                </div>
              </form>
            </CardContentUI>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
